import * as React from 'react';
import { SavedDiscountAction, SavedDiscountBand } from 'app/pages/DiscountEditor/modules/DiscountActions';
import { Currency, Product, DiscountActionTargetTypeEnum, DiscountValueType, DiscountBandTypeEnum } from 'app/shared/model';
import { ProductFilter, ProductFilterType, SelectionMap } from 'app/shared/model/ProductFilter';
import { Fee } from 'app/shared/model/Fee';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { DISCOUNT_MONETARY_DECIMAL_PLACES } from 'app/pages/DiscountEditor/components/EditDiscountActionDialog/discountBandFieldValidation';

interface ActionDescriptionProps {
    action: SavedDiscountAction 
    currency: Currency|undefined 
    getProductByCode: (code: string) => Product|undefined 
    getFeeByCode: (code: string) => Fee|undefined 
    showDeleteConfirmation: (action: SavedDiscountAction) => void
    editAction: (action: SavedDiscountAction) => void
}

export function DiscountActionDescription(props: ActionDescriptionProps)
{
    const bands = getBands(props.action, props.currency);
    const targetItems = getTargetItems(props.action, props.getFeeByCode, props.getProductByCode);

    return (
        <div key={props.action.id}>
            <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>
                {props.action.longName}
            </div>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 24,
                }}
            >
                <div style={{ flex: 1, minWidth: 0 }}>
                    {bands.map((band, index) => (
                        <div key={index} style={{ marginBottom: index < bands.length - 1 ? 4 : 0 }}>
                            • {band}
                        </div>
                    ))}
                </div>
                {targetItems.length > 0 && (
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {targetItems.map((item, index) => (
                            <div key={index} style={{ marginBottom: index < targetItems.length - 1 ? 4 : 0 }}>
                                • {item}
                            </div>
                        ))}
                    </div>
                )}
                <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                    <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => props.showDeleteConfirmation(props.action)}
                        style={{ padding: '4px 8px' }}
                    />
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => props.editAction(props.action)}
                        style={{ padding: '4px 8px' }}
                    />
                </div>
            </div>
        </div>
    );
}

function getTargetItems(
    action: SavedDiscountAction,
    getFeeByCode: (code: string) => Fee|undefined,
    getProductByCode: (code: string) => Product|undefined
): string[]
{
    switch(action.target.type)
    {
        case DiscountActionTargetTypeEnum.Fees:
            return getFeeNames(action.target.fees, getFeeByCode);

        case DiscountActionTargetTypeEnum.OtherProducts:
            return getSpecificProductNames(action.target.products, getProductByCode);

        case DiscountActionTargetTypeEnum.ApplicableProducts:
        default:
            return [];
    }
}

function getFeeNames(
    selectedFees: SelectionMap,
    getFeeByCode: (code: string) => Fee|undefined
): string[] {
    return Object.keys(selectedFees).map(code => {
        const fee = getFeeByCode(code);
        return fee
            ? fee.name
            : 'Unknown fee';
    });
}

function getSpecificProductNames(
    productFilter: ProductFilter,
    getProductByCode: (code: string) => Product|undefined
): string[] {
    switch(productFilter.type)
    {
        case ProductFilterType.IncludeSpecific:
        case ProductFilterType.ExcludeSpecific:
            return Object.keys(productFilter.productCodes).map(code => {
                const product = getProductByCode(code);
                return product ? product.name : (code || 'Unknown product');
            });
        case ProductFilterType.AnyProduct:
        case ProductFilterType.ByType:
        case ProductFilterType.Residence:
        case ProductFilterType.Homestay:
        default:
            return [];
    }
}

function getBands(props: SavedDiscountAction, currency: Currency|undefined) : string[]
{
    const allowedNumberOfItems = 3
    const sortedBands = props.bands;
    let bands : string[];
    let dummyItems = 0;
    
    if (props.bandType === DiscountBandTypeEnum.Discrete) {
        bands = sortedBands.slice(0, allowedNumberOfItems).map((band, i) => {
            const unitRange = ((i + 1) < sortedBands.length)
                ? band.minUnits + '-' + (sortedBands[i+1].minUnits-1) + ' units'
                :  band.minUnits.toString() + '+ units'; 
            return unitRange + ' : ' + getDiscountValue(props.valueType, band.value, currency);
        });
    }
    else if (props.bandType === DiscountBandTypeEnum.Compound) {
        let startAt : number;
        bands = [];
        dummyItems = sortedBands[0].minUnits > 1 ? 1 : 0;
        if (dummyItems) {
            bands.push('first ' + (sortedBands[0].minUnits - 1) + ' units : no discount');
            startAt = 0;
        }
        else {
            bands.push('first ' + (sortedBands[0].maxUnits || 9999).toString() + ' units : ' +
                getDiscountValue(props.valueType, sortedBands[0].value, currency));
            startAt = 1;
        }

        sortedBands.slice(startAt, allowedNumberOfItems - dummyItems).map(b => {
            const unitCount = (b.maxUnits || 9999) - b.minUnits + 1;
            const unitDesc = 'next ' + unitCount.toString() + ' units : ' +
                getDiscountValue(props.valueType, b.value, currency);
            bands.push(unitDesc);
        });
    }
    else {
        throw new Error('Unknown band type');
    }

    if (sortedBands.length > allowedNumberOfItems) {
        bands.push('+ ' + (sortedBands.length - allowedNumberOfItems + dummyItems).toString() + ' other bands...'); 
    }

    return bands;
}

function formatMoney(currency: Currency|undefined, amount: number) : string {
    const symbol = currency ? currency.symbol : '';
    const currencyCode = currency?.code ? ' ' + currency.code : '';
    if (!Number.isFinite(amount)) {
        return symbol + '0' + currencyCode;
    }
    const p = DISCOUNT_MONETARY_DECIMAL_PLACES;
    const scale = 10 ** p;
    const scaled = BigInt(Math.round(amount * scale));
    const negative = scaled < 0n;
    const abs = negative ? -scaled : scaled;
    const intPart = abs / BigInt(scale);
    const frac = abs % BigInt(scale);
    let fracStr = frac.toString().padStart(p, '0');
    fracStr = fracStr.replace(/0+$/, '');
    const core =
        fracStr.length === 0 ? intPart.toString() : `${intPart.toString()}.${fracStr}`;
    const signed = negative ? `-${core}` : core;
    return symbol + signed + currencyCode;
}

function getDiscountValue(
    valueType: DiscountValueType, 
    amount: number, 
    currency: Currency|undefined) : string
{
    switch (valueType) {
        case DiscountValueType.Percent: 
            return amount.toString() + "% off"; 
        case DiscountValueType.AmountPerUnit:
            return formatMoney(currency, amount) + " per unit"
        case DiscountValueType.OverrideUnitPrice:
            return formatMoney(currency, amount) + " unit price";
        default: 
            return amount.toString() + " off"; 
    }
}
